export const VALVE_OPEN_FAILURE_COLOR = '#FF8C00'
export const VALVE_CLOSE_FAILURE_COLOR = '#FFD600'
// green: waiting for pressure is a normal state at every opening, not an error.
// This shade has enough contrast to the dark symbol of the map badge and to white backgrounds (popup, overlay)
export const VALVE_INPUT_PENDING_COLOR = '#34A853'

export const VALVE_BADGE_SYMBOLS = {
  // valve did not open/close after the delay => error
  FAILURE: 'failure',
  // opened valve is still waiting for pressure => not an error (yet)
  PENDING: 'pending',
}

export const getMarkerFillColor = ({
  online,
  hasOpenValve,
  hasValveInputPending,
}) => {
  if (online === false) {
    return '#FF0000'
  }

  if (hasValveInputPending) {
    return VALVE_INPUT_PENDING_COLOR
  }

  return hasOpenValve ? '#1647F3' : 'white'
}

export const getMarkerStrokeColor = ({
  online,
  hasUnreadError,
  hasValveOpenFailure,
  hasValveCloseFailure,
}) => {
  if (!hasUnreadError && !hasValveOpenFailure && !hasValveCloseFailure) {
    return 'white'
  }

  return online === false ? '#FFB000' : '#FF0000'
}

// Returns the badge for the most important valve state of a module:
// open failure > close failure > waiting for pressure.
// A valve that did not open is more serious than one that did not close,
// so only the open failure is shown when both occur on the same module.
// Waiting for pressure is not shown for offline modules, because the state
// can't change anymore until the module sends a status again.
export const getValveBadge = ({
  online,
  hasValveOpenFailure,
  hasValveCloseFailure,
  hasValveInputPending,
}) => {
  if (hasValveOpenFailure) {
    return {
      color: VALVE_OPEN_FAILURE_COLOR,
      symbol: VALVE_BADGE_SYMBOLS.FAILURE,
    }
  }

  if (hasValveCloseFailure) {
    return {
      color: VALVE_CLOSE_FAILURE_COLOR,
      symbol: VALVE_BADGE_SYMBOLS.FAILURE,
    }
  }

  if (hasValveInputPending && online !== false) {
    return {
      color: VALVE_INPUT_PENDING_COLOR,
      symbol: VALVE_BADGE_SYMBOLS.PENDING,
    }
  }

  return null
}

export const getValveStateBadge = ({ inputMismatch, inputPending }) =>
  getValveBadge({
    hasValveOpenFailure: inputMismatch === 'open_failed',
    hasValveCloseFailure: inputMismatch === 'close_failed',
    hasValveInputPending: inputPending,
  })

// Returns the valve state flags of a module from its valves. The valves are live updated,
// the module itself is only fetched when the map is loaded, so the marker uses these flags.
// Returns an empty object without valves (e.g. not loaded yet), so the flags of the module are kept.
export const getValveStateFlags = (valves = []) => {
  if (valves.length === 0) {
    return {}
  }

  return {
    hasOpenValve: valves.some(({ currentValue }) => Number(currentValue) === 1),
    hasSuspendedValve: valves.some(
      ({ currentIsSuspended }) => !!currentIsSuspended
    ),
    hasValveOpenFailure: valves.some(
      ({ inputMismatch }) => inputMismatch === 'open_failed'
    ),
    hasValveCloseFailure: valves.some(
      ({ inputMismatch }) => inputMismatch === 'close_failed'
    ),
    hasValveInputPending: valves.some(({ inputPending }) => !!inputPending),
  }
}
